import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25ChatComponent } from "../s25.chat.component";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";
import { S25IconButtonComponent } from "../s25.icon.button.component";
import { DraggableDirective } from "../../modules/s25-movable/s25.movable.element.directive";
import { SocChatExternalType } from "../../services/standout.classroom.service";
import { EventChatStore } from "./event.chat.store";
import { S25Util } from "../../util/s25-util";

@TypeManagerDecorator("s25-ng-event-chat-panel")
@Component({
    selector: "s25-ng-event-chat-panel",
    standalone: true,
    imports: [S25ChatComponent, S25IconComponent, S25IconButtonComponent, DraggableDirective],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (store.chatActive()) {
            <div draggableElement class="chat-panel" [class.minimized]="store.chatMinimized()">
                <div class="chat-header" data-drag-handle>
                    <div class="header-left">
                        <s25-ng-icon [type]="'chat'" [ariaLabel]="false"></s25-ng-icon>
                        <h3>{{ store.chatTitle() }}</h3>
                    </div>
                    <div class="header-controls">
                        @if (store.chatLoading()) {
                            <span class="chat-loading-spinner">Loading...</span>
                        }
                        <s25-ng-icon-button
                            class="minimize-btn"
                            [type]="store.chatMinimized() ? 'expand' : 'collapse'"
                            [ariaLabel]="store.chatMinimized() ? 'Expand chat' : 'Minimize chat'"
                            [size]="30"
                            (clicked)="$event.stopPropagation(); store.chatMinimized.set(!store.chatMinimized())"
                        ></s25-ng-icon-button>
                        <s25-ng-icon-button
                            [type]="'close'"
                            [ariaLabel]="'Close chat'"
                            [size]="20"
                            (clicked)="$event.stopPropagation(); store.closeChat()"
                        ></s25-ng-icon-button>
                    </div>
                </div>
                <div class="chat-content" [class.hidden]="store.chatMinimized()">
                    @if (!store.chatLoading()) {
                        <s25-chat
                            [channelId]="store.channelKey()"
                            [channelTitle]="store.channelTitle()"
                            [channelType]="store.channelType()"
                            [socChannelId]="store.socChannelId()"
                            [externalType]="SocChatExternalType.Event"
                        ></s25-chat>
                    }
                </div>
            </div>
        }
    `,
    host: {
        "[class.mobile]": "this.mobile",
    },
    styles: `
        .chat-panel {
            display: flex;
            flex-direction: column;
            width: 600px;
            height: 600px;
            background: var(--color--page-bg);
            border-radius: var(--border-radius);
            overflow: hidden;
            border: 1px solid var(--color--border-bold);
        }

        .chat-panel.minimized {
            height: auto !important;
            resize: none;
        }

        .chat-header {
            cursor: move;
            padding: 12px 16px;
            background: var(--color--surface-bg);
            border-bottom: 1px solid var(--color--border-bold);
            display: flex;
            justify-content: space-between;
            align-items: center;
            cursor: move;
        }

        .header-left {
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .header-left h3 {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
            color: var(--color--text-strong);
        }

        .header-controls {
            display: flex;
            gap: 4px;
            align-items: flex-start;
        }

        .chat-loading-spinner {
            font-size: 12px;
            color: var(--color--text-muted);
            margin-right: 8px;
        }

        .chat-content {
            flex: 1;
            height: 0;
            background: var(--color--surface-bg);
            overflow: hidden;
        }

        .chat-content.hidden {
            display: none;
        }

        :host.mobile {
            .chat-panel {
                position: fixed;
                inset: 0;
                width: auto;
                height: auto;
                border-radius: 0;
                resize: none;
                padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom)
                    env(safe-area-inset-left);
                background-color: var(--color--surface-bg);
            }

            .chat-panel.minimized {
                height: auto;
                inset: auto 0 0;
            }

            .chat-header {
                cursor: default;
            }

            .minimize-btn {
                display: none;
            }
        }
    `,
})
export class EventChatPanelComponent {
    protected readonly store = inject(EventChatStore);
    protected readonly SocChatExternalType = SocChatExternalType;
    protected readonly mobile = S25Util.mobileCheck();
}
